import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { EffektCheckbox } from "../../../../shared/components/EffektCheckbox/EffektCheckbox";
import { NavLink } from "../../../../shared/components/Navbar/Navbar";
import { Spinner } from "../../../../shared/components/Spinner/Spinner";
import { PaymentMethodSelector } from "../components/PaymentMethodSelector";
import { FormData } from "../hooks/useFundraiserForm";
import styles from "../FundraiserWidget.module.scss";
import Link from "next/link";
import { validateOrg, validateSsn } from "@ssfbank/norwegian-id-validators";
import Organisationsnummer from "organisationsnummer";
import Personnummer from "personnummer";
import { validateTin, formatTinInput } from "../../../../../util/tin-validation";
import { FormattingLocale } from "../../../../../util/formatting";
import { fundraiserSubmitButtonText } from "../fundraiserSubmitButtonText";
import { RecurringDonation } from "../../../../shared/components/Widget/types/Enums";
import { formatDateText } from "../../../../shared/components/Widget/components/panes/PaymentPane/AvtaleGiro/AvtaleGiroDatePicker/avtalegirodates";
import { getFirstAvtaleGiroCharge } from "../avtaleGiroFirstCharge";
interface PaymentMethodPaneProps {
  formData: Pick<FormData, "taxDeduction" | "newsletter" | "email" | "ssn" | "paymentMethod" | "privacyPolicyAccepted" | "recurring">;
  onChange: (field: keyof FormData, value: any) => void;
  onSubmit: () => void;
  loading: boolean;
  error?: string | null;
  config: {
    allow_anonymous_donations: boolean;
    tax_deduction?: {
      label: string;
      tooltip_text: string;
      ssn_label: string;
      ssn_invalid_error_text: string;
      ssn_suspicious_error_text: string;
    } | null;
    newsletter?: {
      label: string;
    } | null;
    email_label: string;
    privacy_policy: {
      text: string;
      require_checkbox?: boolean;
      required_error_text?: string;
      privacy_policy_url?: NavLink;
    };
    payment_methods: Array<{
      _type: string;
      selector_text: string | null;
      single_button_text: string | null;
      recurring_button_text: string | null;
      button_text: string | null;
    }>;
  };
  className?: string;
  visible?: boolean;
  privacyPolicyError?: boolean;
  locale: FormattingLocale;
}
const validateSsnNo = (ssn: string): boolean => {
  return ssn.length === 9 && validateOrg(ssn) || ssn.length === 11 && validateSsn(ssn);
};
const validateSsnSe = (ssn: string): boolean => {
  return Personnummer.valid(ssn) || Organisationsnummer.valid(ssn);
};
export const PaymentMethodPane = React.forwardRef<HTMLDivElement, PaymentMethodPaneProps>((t0, ref) => {
  const $ = _c(130);
  const {
    formData,
    onChange,
    onSubmit,
    loading,
    error,
    config,
    className,
    visible,
    privacyPolicyError,
    locale
  } = t0;
  const [ssnError, setSsnError] = useState(null);
  const [cprSuspicious, setCprSuspicious] = useState(false);
  const [prevTaxDeduction, setPrevTaxDeduction] = useState(formData.taxDeduction);
  if (formData.taxDeduction !== prevTaxDeduction) {
    setPrevTaxDeduction(formData.taxDeduction);
    if (!formData.taxDeduction) {
      setSsnError(null);
      setCprSuspicious(false);
    }
  }
  let t1;
  if ($[0] !== config.tax_deduction?.ssn_invalid_error_text || $[1] !== formData.taxDeduction || $[2] !== locale || $[3] !== onChange) {
    t1 = e => {
      const {
        value
      } = e.target;
      let valueToUse = value;
      if (locale === "da-DK" && formData.taxDeduction) {
        const formattedValue = formatTinInput(value, {
          allowCvr: true
        });
        e.target.value = formattedValue;
        valueToUse = formattedValue;
        onChange("ssn", formattedValue);
      } else {
        onChange("ssn", value);
      }
      const trimmed = valueToUse.trim();
      if (!formData.taxDeduction || trimmed === "") {
        setSsnError(null);
        setCprSuspicious(false);
        return;
      }
      let isValid = true;
      if (locale === "no-NB") {
        isValid = validateSsnNo(trimmed);
      } else {
        if (locale === "sv-SE") {
          isValid = validateSsnSe(trimmed);
        } else {
          if (locale === "da-DK") {
            const validationResult = validateTin(trimmed, {
              allowCvr: true
            });
            if (validationResult.type === "CPR") {
              if (validationResult.isSuspicious) {
                setCprSuspicious(true);
              } else {
                if (validationResult.isValid && !validationResult.isSuspicious) {
                  setCprSuspicious(false);
                }
              }
            } else {
              setCprSuspicious(false);
            }
            isValid = validationResult.isValid;
          }
        }
      }
      setSsnError(isValid ? null : config.tax_deduction?.ssn_invalid_error_text || null);
    };
    $[0] = config.tax_deduction?.ssn_invalid_error_text;
    $[1] = formData.taxDeduction;
    $[2] = locale;
    $[3] = onChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleSsnChange = t1;
  let t2;
  if ($[5] !== config.tax_deduction?.ssn_invalid_error_text || $[6] !== formData.ssn || $[7] !== formData.taxDeduction || $[8] !== locale) {
    t2 = () => {
      const trimmed_0 = formData.ssn.trim();
      if (!formData.taxDeduction || trimmed_0 === "") {
        setSsnError(null);
        setCprSuspicious(false);
        return;
      }
      let isValid_0 = true;
      if (locale === "no-NB") {
        isValid_0 = validateSsnNo(trimmed_0);
      } else {
        if (locale === "sv-SE") {
          isValid_0 = validateSsnSe(trimmed_0);
        } else {
          if (locale === "da-DK") {
            const validationResult_0 = validateTin(trimmed_0, {
              allowCvr: true
            });
            if (validationResult_0.type === "CPR") {
              if (validationResult_0.isSuspicious) {
                setCprSuspicious(true);
              } else {
                if (validationResult_0.isValid && !validationResult_0.isSuspicious) {
                  setCprSuspicious(false);
                }
              }
            } else {
              setCprSuspicious(false);
            }
            isValid_0 = validationResult_0.isValid;
          }
        }
      }
      setSsnError(isValid_0 ? null : config.tax_deduction?.ssn_invalid_error_text || null);
    };
    $[5] = config.tax_deduction?.ssn_invalid_error_text;
    $[6] = formData.ssn;
    $[7] = formData.taxDeduction;
    $[8] = locale;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const validateSsnOnBlur = t2;
  const isMonthly = formData.recurring === RecurringDonation.RECURRING;
  const emailRequired = locale === "da-DK" || formData.taxDeduction || formData.newsletter || isMonthly;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== className || $[11] !== config.allow_anonymous_donations || $[12] !== config.email_label || $[13] !== config.newsletter || $[14] !== config.payment_methods || $[15] !== config.privacy_policy.privacy_policy_url || $[16] !== config.privacy_policy.require_checkbox || $[17] !== config.privacy_policy.required_error_text || $[18] !== config.privacy_policy.text || $[19] !== config.tax_deduction || $[20] !== cprSuspicious || $[21] !== emailRequired || $[22] !== formData.email || $[23] !== formData.newsletter || $[24] !== formData.paymentMethod || $[25] !== formData.privacyPolicyAccepted || $[26] !== formData.ssn || $[27] !== formData.taxDeduction || $[28] !== handleSsnChange || $[29] !== isMonthly || $[30] !== locale || $[31] !== onChange || $[32] !== onSubmit || $[33] !== privacyPolicyError || $[34] !== ref || $[35] !== ssnError || $[36] !== validateSsnOnBlur || $[37] !== visible) {
    const avtaleGiroFirstCharge = isMonthly && formData.paymentMethod === "bank" && locale === "no-NB" ? getFirstAvtaleGiroCharge() : null;
    let t18;
    if ($[53] !== onChange) {
      t18 = e_0 => onChange("email", e_0.target.value);
      $[53] = onChange;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    let t19;
    if ($[55] !== config.email_label || $[56] !== emailRequired || $[57] !== formData.email || $[58] !== t18) {
      t19 = <div className={styles["donation-widget__input-group"]}><input id="email" name="email" type="email" value={formData.email} onChange={t18} required={emailRequired} placeholder={config.email_label} data-cy="fundraiser-email-input" /></div>;
      $[55] = config.email_label;
      $[56] = emailRequired;
      $[57] = formData.email;
      $[58] = t18;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    const emailInput = t19;
    t14 = ref;
    t15 = className;
    const t20 = visible ? "block" : "none";
    if ($[60] !== t20) {
      t16 = {
        display: t20
      };
      $[60] = t20;
      $[61] = t16;
    } else {
      t16 = $[61];
    }
    t17 = "fundraiser-pane-payment";
    t3 = styles["donation-widget__form"];
    t4 = "fundraiser-payment-form";
    if ($[62] !== config.tax_deduction?.ssn_invalid_error_text || $[63] !== formData.ssn || $[64] !== formData.taxDeduction || $[65] !== locale || $[66] !== onSubmit) {
      t5 = e_1 => {
        e_1.preventDefault();
        if (formData.taxDeduction && formData.ssn.trim()) {
          const trimmed_1 = formData.ssn.trim();
          let isValid_1 = true;
          if (locale === "no-NB") {
            isValid_1 = validateSsnNo(trimmed_1);
          } else {
            if (locale === "sv-SE") {
              isValid_1 = validateSsnSe(trimmed_1);
            } else {
              if (locale === "da-DK") {
                const validationResult_1 = validateTin(trimmed_1, {
                  allowCvr: true
                });
                isValid_1 = validationResult_1.isValid;
              }
            }
          }
          if (!isValid_1) {
            setSsnError(config.tax_deduction?.ssn_invalid_error_text || null);
            return;
          }
        }
        onSubmit();
      };
      $[62] = config.tax_deduction?.ssn_invalid_error_text;
      $[63] = formData.ssn;
      $[64] = formData.taxDeduction;
      $[65] = locale;
      $[66] = onSubmit;
      $[67] = t5;
    } else {
      t5 = $[67];
    }
    t6 = (!config.allow_anonymous_donations || isMonthly) && emailInput;
    if ($[68] !== config.tax_deduction || $[69] !== formData.taxDeduction || $[70] !== onChange) {
      t7 = config.tax_deduction && <div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.taxDeduction} onChange={checked => {
          onChange("taxDeduction", checked);
        }} dataCy="fundraiser-tax-deduction">{config.tax_deduction.label}</EffektCheckbox><span className={styles["donation-widget__checkbox-group__popup-trigger"]}>?</span>{" "}<div className={styles["donation-widget__checkbox-group__popup"]}>{config.tax_deduction.tooltip_text}</div></div>;
      $[68] = config.tax_deduction;
      $[69] = formData.taxDeduction;
      $[70] = onChange;
      $[71] = t7;
    } else {
      t7 = $[71];
    }
    if ($[72] !== config.newsletter || $[73] !== formData.newsletter || $[74] !== onChange) {
      t8 = config.newsletter && <div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.newsletter} onChange={checked_0 => {
          onChange("newsletter", checked_0);
        }} dataCy="fundraiser-newsletter">{config.newsletter.label}</EffektCheckbox></div>;
      $[72] = config.newsletter;
      $[73] = formData.newsletter;
      $[74] = onChange;
      $[75] = t8;
    } else {
      t8 = $[75];
    }
    t9 = (formData.taxDeduction || formData.newsletter) && config.allow_anonymous_donations && !isMonthly && emailInput;
    if ($[76] !== config.tax_deduction || $[77] !== cprSuspicious || $[78] !== formData.ssn || $[79] !== formData.taxDeduction || $[80] !== handleSsnChange || $[81] !== locale || $[82] !== ssnError || $[83] !== validateSsnOnBlur) {
      t10 = formData.taxDeduction && config.tax_deduction && <div className={styles["donation-widget__input-group"]}><input id="ssn" name="ssn" type="text" inputMode="numeric" pattern={locale === "da-DK" ? undefined : "[0-9]*"} value={formData.ssn} onChange={handleSsnChange} onBlur={validateSsnOnBlur} required={formData.taxDeduction} placeholder={config.tax_deduction.ssn_label} autoComplete="off" data-cy="fundraiser-ssn-input" />{ssnError && <div style={{
          color: "var(--primary)",
          fontSize: "14px",
          marginTop: "5px"
        }}>{ssnError}</div>}{cprSuspicious && locale === "da-DK" && <div style={{
          color: "var(--primary)",
          fontSize: "14px",
          marginTop: "5px"
        }}>{config.tax_deduction?.ssn_suspicious_error_text}</div>}</div>;
      $[76] = config.tax_deduction;
      $[77] = cprSuspicious;
      $[78] = formData.ssn;
      $[79] = formData.taxDeduction;
      $[80] = handleSsnChange;
      $[81] = locale;
      $[82] = ssnError;
      $[83] = validateSsnOnBlur;
      $[84] = t10;
    } else {
      t10 = $[84];
    }
    let t21;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = {
        marginTop: "2rem"
      };
      $[85] = t21;
    } else {
      t21 = $[85];
    }
    if ($[86] !== config.privacy_policy.privacy_policy_url || $[87] !== config.privacy_policy.require_checkbox || $[88] !== config.privacy_policy.required_error_text || $[89] !== config.privacy_policy.text || $[90] !== formData.privacyPolicyAccepted || $[91] !== onChange || $[92] !== privacyPolicyError) {
      t11 = <div style={t21}>{config.privacy_policy.require_checkbox ? <><div className={styles["donation-widget__privacy-checkbox-group"]}><div className={styles["donation-widget__checkbox-group"]}><EffektCheckbox checked={formData.privacyPolicyAccepted} onChange={checked_1 => {
                onChange("privacyPolicyAccepted", checked_1);
              }} dataCy="fundraiser-privacy-checkbox">{`${config.privacy_policy.text}`}</EffektCheckbox></div>{config.privacy_policy.privacy_policy_url && <Link href={`/${config.privacy_policy.privacy_policy_url.slug}`} target="_blank">{` ${config.privacy_policy.privacy_policy_url.title} ↗`}</Link>}</div>{privacyPolicyError && <div style={{
            color: "var(--primary)",
            fontSize: "14px",
            marginTop: "5px"
          }}>{config.privacy_policy.required_error_text}</div>}</> : <div className={styles["donation-widget__privacy-link"]}><span>{config.privacy_policy.text}</span>{config.privacy_policy.privacy_policy_url && <Link href={`/${config.privacy_policy.privacy_policy_url.slug}`} target="_blank">{` ${config.privacy_policy.privacy_policy_url.title} ↗`}</Link>}</div>}</div>;
      $[86] = config.privacy_policy.privacy_policy_url;
      $[87] = config.privacy_policy.require_checkbox;
      $[88] = config.privacy_policy.required_error_text;
      $[89] = config.privacy_policy.text;
      $[90] = formData.privacyPolicyAccepted;
      $[91] = onChange;
      $[92] = privacyPolicyError;
      $[93] = t11;
    } else {
      t11 = $[93];
    }
    let t22;
    if ($[94] !== onChange) {
      t22 = method => onChange("paymentMethod", method);
      $[94] = onChange;
      $[95] = t22;
    } else {
      t22 = $[95];
    }
    if ($[96] !== config.payment_methods || $[97] !== formData.paymentMethod || $[98] !== t22) {
      t12 = <div className={styles["donation-widget__payment-options"]}><PaymentMethodSelector methods={config.payment_methods} selected={formData.paymentMethod} onSelect={t22} /></div>;
      $[96] = config.payment_methods;
      $[97] = formData.paymentMethod;
      $[98] = t22;
      $[99] = t12;
    } else {
      t12 = $[99];
    }
    t13 = avtaleGiroFirstCharge && <p className={styles["donation-widget__first-charge"]} data-cy="fundraiser-avtalegiro-first-charge">{`Første trekk blir rundt ${formatDateText(avtaleGiroFirstCharge.firstChargeDate)}, deretter den ${avtaleGiroFirstCharge.dueDay}. hver måned.`}</p>;
    $[10] = className;
    $[11] = config.allow_anonymous_donations;
    $[12] = config.email_label;
    $[13] = config.newsletter;
    $[14] = config.payment_methods;
    $[15] = config.privacy_policy.privacy_policy_url;
    $[16] = config.privacy_policy.require_checkbox;
    $[17] = config.privacy_policy.required_error_text;
    $[18] = config.privacy_policy.text;
    $[19] = config.tax_deduction;
    $[20] = cprSuspicious;
    $[21] = emailRequired;
    $[22] = formData.email;
    $[23] = formData.newsletter;
    $[24] = formData.paymentMethod;
    $[25] = formData.privacyPolicyAccepted;
    $[26] = formData.ssn;
    $[27] = formData.taxDeduction;
    $[28] = handleSsnChange;
    $[29] = isMonthly;
    $[30] = locale;
    $[31] = onChange;
    $[32] = onSubmit;
    $[33] = privacyPolicyError;
    $[34] = ref;
    $[35] = ssnError;
    $[36] = validateSsnOnBlur;
    $[37] = visible;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
  } else {
    t10 = $[38];
    t11 = $[39];
    t12 = $[40];
    t13 = $[41];
    t14 = $[42];
    t15 = $[43];
    t16 = $[44];
    t17 = $[45];
    t3 = $[46];
    t4 = $[47];
    t5 = $[48];
    t6 = $[49];
    t7 = $[50];
    t8 = $[51];
    t9 = $[52];
  }
  const t18 = loading || undefined;
  let t19;
  if ($[100] !== formData.paymentMethod || $[101] !== formData.recurring || $[102] !== loading) {
    t19 = loading ? <Spinner className={styles["donation-widget__spinner"]} /> : fundraiserSubmitButtonText(formData.paymentMethod, formData.recurring);
    $[100] = formData.paymentMethod;
    $[101] = formData.recurring;
    $[102] = loading;
    $[103] = t19;
  } else {
    t19 = $[103];
  }
  let t20;
  if ($[104] !== loading || $[105] !== t18 || $[106] !== t19) {
    t20 = <button type="submit" className={styles["donation-widget__button"]} data-cy="fundraiser-submit-button" disabled={loading} aria-busy={t18}>{t19}</button>;
    $[104] = loading;
    $[105] = t18;
    $[106] = t19;
    $[107] = t20;
  } else {
    t20 = $[107];
  }
  let t21;
  if ($[108] !== error) {
    t21 = error && <div role="alert" data-cy="fundraiser-submit-error" style={{
      color: "var(--primary)",
      fontSize: "14px",
      marginTop: "-1.5rem"
    }}>{error}</div>;
    $[108] = error;
    $[109] = t21;
  } else {
    t21 = $[109];
  }
  let t22;
  if ($[110] !== t10 || $[111] !== t11 || $[112] !== t12 || $[113] !== t13 || $[114] !== t20 || $[115] !== t21 || $[116] !== t3 || $[117] !== t4 || $[118] !== t5 || $[119] !== t6 || $[120] !== t7 || $[121] !== t8 || $[122] !== t9) {
    t22 = <form className={t3} data-cy={t4} onSubmit={t5}>{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t20}{t21}</form>;
    $[110] = t10;
    $[111] = t11;
    $[112] = t12;
    $[113] = t13;
    $[114] = t20;
    $[115] = t21;
    $[116] = t3;
    $[117] = t4;
    $[118] = t5;
    $[119] = t6;
    $[120] = t7;
    $[121] = t8;
    $[122] = t9;
    $[123] = t22;
  } else {
    t22 = $[123];
  }
  let t23;
  if ($[124] !== t14 || $[125] !== t15 || $[126] !== t16 || $[127] !== t17 || $[128] !== t22) {
    t23 = <div ref={t14} className={t15} style={t16} data-cy={t17}>{t22}</div>;
    $[124] = t14;
    $[125] = t15;
    $[126] = t16;
    $[127] = t17;
    $[128] = t22;
    $[129] = t23;
  } else {
    t23 = $[129];
  }
  return t23;
});
PaymentMethodPane.displayName = "PaymentMethodPane";